import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  BarChart3, TrendingUp, TrendingDown, Minus, 
  Eye, MousePointer, Target, Calendar,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapping notification types to French labels
const NOTIFICATION_LABELS = {
  'AG_CREATED': 'Création d\'AG',
  'COPRO_ADDED': 'Ajout coproprietaire',
  'BUILDING_CREATED': 'Création immeuble',
  'INCIDENT_CREATED': 'Signalement incident',
  'INCIDENT_RESOLVED': 'Résolution incident',
  'RESOLUTION_ADDED': 'Ajout résolution',
  'MEMBER_ADDED': 'Ajout membre équipe',
  'EVENT_CREATED': 'Création événement',
  'PAYMENT_SUCCESS': 'Paiement réussi',
  'PROFILE_UPDATED': 'Mise à jour profil',
  'DOCUMENT_UPLOADED': 'Document téléversé',
  'EXERCICE_CREATED': 'Exercice comptable',
  'AG_CLOSED': 'Clôture AG',
  'IMPORT_COPRO_SUCCESS': 'Import coproprietaires',
  'IMPORT_BUILDING_SUCCESS': 'Import immeubles'
};

const NotificationAnalytics = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [token, period]);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/stats/notifications?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/stats/notifications/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getLabel = (type) => NOTIFICATION_LABELS[type] || type;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
            <div className="h-96 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={fetchStats}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Statistiques des notifications
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Analysez l'engagement des utilisateurs avec les notifications de succès
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              data-testid="period-select"
            >
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="90d">90 derniers jours</option>
              <option value="all">Tout</option>
            </select>
            <button
              onClick={fetchStats}
              className="p-2 text-slate-500 hover:text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg transition-colors"
              data-testid="refresh-stats-btn"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Views */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Eye className="w-5 h-5 text-blue-500" />
              </div>
              {summary && getTrendIcon(summary.trend_direction)}
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold text-slate-800">
                {summary?.total_views_30d || 0}
              </p>
              <p className="text-sm text-slate-500">Notifications affichées</p>
            </div>
            {summary?.trend_vs_previous !== 0 && (
              <p className={`text-xs mt-2 ${summary?.trend_direction === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {summary?.trend_direction === 'up' ? '+' : ''}{summary?.trend_vs_previous}% vs période précédente
              </p>
            )}
          </div>

          {/* Click Rate */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-green-50 rounded-lg">
                <MousePointer className="w-5 h-5 text-green-500" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold text-slate-800">
                {summary?.click_rate || 0}%
              </p>
              <p className="text-sm text-slate-500">Taux de clic "Prochaine étape"</p>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {summary?.total_clicks_30d || 0} clics sur {summary?.total_views_30d || 0} vues
            </p>
          </div>

          {/* Best Performer */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-amber-50 rounded-lg">
                <Target className="w-5 h-5 text-amber-500" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-lg font-bold text-slate-800 truncate">
                {stats?.top_performing ? getLabel(stats.top_performing) : '-'}
              </p>
              <p className="text-sm text-slate-500">Meilleur taux de conversion</p>
            </div>
          </div>

          {/* Needs Improvement */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-red-50 rounded-lg">
                <BarChart3 className="w-5 h-5 text-red-500" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-lg font-bold text-slate-800 truncate">
                {stats?.needs_improvement ? getLabel(stats.needs_improvement) : '-'}
              </p>
              <p className="text-sm text-slate-500">À améliorer</p>
            </div>
          </div>
        </div>

        {/* Detailed Stats Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800">
              Détail par type de notification
            </h2>
          </div>
          
          {stats?.notifications && stats.notifications.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Type de notification
                    </th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Vues
                    </th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Clics
                    </th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Taux de clic
                    </th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Moy./jour
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stats.notifications.map((notif, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <span className="font-medium text-slate-700">
                          {getLabel(notif.notification_type)}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right text-slate-600">
                        {notif.total_views}
                      </td>
                      <td className="px-5 py-4 text-right text-slate-600">
                        {notif.next_step_clicks}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          notif.click_rate >= 50 
                            ? 'bg-green-100 text-green-800' 
                            : notif.click_rate >= 25 
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {notif.click_rate}%
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right text-slate-500 text-sm">
                        {notif.avg_per_day}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-10 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune donnée pour cette période</p>
              <p className="text-sm text-slate-400 mt-1">
                Les statistiques apparaîtront quand des notifications seront affichées
              </p>
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-[#00D3A8]/5 to-[#00E5BB]/5 rounded-xl p-5 border border-[#00D3A8]/10">
          <h3 className="font-semibold text-slate-800 mb-2">
            Comment améliorer l'engagement ?
          </h3>
          <ul className="text-sm text-slate-600 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-[#00D3A8]">•</span>
              <span>Les notifications avec un taux de clic {">"} 50% indiquent des suggestions pertinentes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00D3A8]">•</span>
              <span>Un faible taux de clic peut indiquer que la prochaine étape suggérée n'est pas claire</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00D3A8]">•</span>
              <span>Surveillez les notifications les plus fréquentes pour optimiser le parcours utilisateur</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NotificationAnalytics;
